/**
 * Real `wageproAuth.organisationStatus` resolver — powers the post-consent
 * landing decision (issue #23952). Same "self-fetching hook + pure decision
 * function" split every other real hook in this app uses
 * (`use_session_facts.ts`, `use_my_pay.ts`, …). No dev-bypass fan-out; live
 * in every environment.
 *
 * Uses the WagePro admin console's default cookie-based tRPC client
 * (`getTrpcClient()`, `lib/trpc.ts`) — the same client `useSessionFacts()`
 * uses — because both procedures on this endpoint are on the WagePro OAuth
 * allowlist AND accept first-party session-cookie callers (see
 * `wageproAuth.ts`'s module docblock).
 *
 * Reset semantics on entity-context change: the previous fetch is cancelled
 * and both `data`/`failed` are cleared. Same pattern as `use_session_facts.ts`.
 */
import * as React from 'react';
import { getTrpcClient } from '../lib/trpc.js';
import { useOAuthEntityContext } from '../lib/auth/oauth_entity_context.js';
import {
  resolveOrganisationStatusState,
  type OrganisationStatusPayload,
  type OrganisationStatusState,
} from './use_organisation_status_logic.js';

export type {
  OrganisationStatusPayload,
  OrganisationStatusState,
} from './use_organisation_status_logic.js';

export interface UseOrganisationStatusResult {
  state: OrganisationStatusState;
  /** Re-runs the fetch from scratch. Only meaningful for the `error` state. */
  retry: () => void;
}

export interface UseOrganisationStatusOptions {
  /**
   * Skip the query entirely when false — the hook still returns a `no_entity`
   * state (see reducer). Used by {@link ConnectLandingGate} to avoid a
   * round-trip an employee / non-admin firm member can never act on;
   * `shouldQueryOrganisationStatus` in `connect_landing_logic.ts` is the pure
   * decision.
   */
  enabled?: boolean;
}

export function useOrganisationStatus(
  options: UseOrganisationStatusOptions = {},
): UseOrganisationStatusResult {
  const { enabled = true } = options;
  const entityContext = useOAuthEntityContext();
  const [data, setData] = React.useState<OrganisationStatusPayload | undefined>(
    undefined,
  );
  const [failed, setFailed] = React.useState(false);
  const [reloadToken, setReloadToken] = React.useState(0);

  React.useEffect(() => {
    if (!enabled) return;
    if (entityContext.status !== 'ready') return;
    const firmId = entityContext.firm_id;
    const entityId = entityContext.entity_id;
    let cancelled = false;
    setData(undefined);
    setFailed(false);

    async function load(): Promise<void> {
      try {
        const client = getTrpcClient();
        const result = await client.wageproAuth.organisationStatus.query({
          firm_id: firmId,
          entity_id: entityId,
        });
        if (cancelled) return;
        setData({
          connected: result.connected,
          has_payroll_entitlement: result.has_payroll_entitlement,
          company_id: result.company_id,
        });
      } catch (err) {
        if (cancelled) return;
        console.error(
          '[wagepro-web] Failed to resolve WagePro organisation status:',
          err,
        );
        setFailed(true);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [enabled, entityContext, reloadToken]);

  const state = resolveOrganisationStatusState({
    entityContext,
    data,
    failed,
  });

  const retry = React.useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  return { state, retry };
}
